import React, { useMemo } from 'react';
import { Select } from 'antd';
import type { DefaultOptionType } from 'antd/lib/select';
import { useQuery } from '@common/hooks';
import { SHIFT_GAP_TAGS_QUERY } from '@scheduling/graphql';
import { ErrorHandler, Placeholder, Form, Col } from '@common/components';
export const ShiftTagSelect = ({
  defaultValue,
  organizationUnitId,
}: {
  defaultValue?: string[];
  organizationUnitId?: string;
}) => {
  const { data, loading, error } = useQuery(SHIFT_GAP_TAGS_QUERY, {
    variables: { organizationUnitId },
    fetchPolicy: 'cache-and-network',
  });

  const tags = data?.shiftGapTags;

  const groupedOptions = useMemo((): DefaultOptionType[] => {
    if (!tags?.length) return [];

    if (!organizationUnitId) {
      return tags.map(toOption);
    }

    const orgTags = tags.filter((t) => t.scope === 'organization');
    const unitTags = tags.filter((t) => t.scope === 'unit');

    return [
      { label: 'Unit Tags', options: unitTags.map(toOption) },
      { label: 'Organization Tags', options: orgTags.map(toOption) },
    ].filter((g) => g.options.length > 0);
  }, [tags, organizationUnitId]);

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="ShiftTagSelect"
        messageType="contextual"
      />
    );
  }

  if (!tags?.length && !loading) {
    return null;
  }

  return (
    <Col span={24}>
      <Placeholder.Input loading={loading} size="large" width={700}>
        <Form.Item name="shiftGapTagIds" label="Tags">
          <Select
            mode="multiple"
            filterOption={filterOption}
            options={groupedOptions}
            placeholder="Select tags"
            size="large"
            loading={loading}
            defaultValue={defaultValue}
          />
        </Form.Item>
      </Placeholder.Input>
    </Col>
  );
};

function filterOption(input: string, option?: DefaultOptionType) {
  return option
    ? String(option?.label).toLowerCase().indexOf(input.toLowerCase()) >= 0
    : false;
}

function toOption(tag: { id: string; name: string }) {
  return {
    label: tag.name,
    value: tag.id,
  };
}
